import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as TransferIcon,
  Inventory as ProductIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { formatDate, formatTime } from '../../utils/dateFormatter';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

import Layout from '../../components/layout/Layout';
import axiosInstance from '../../utils/axios';
import { getCurrentUser } from '../../services/authService';

// Format date for display using regional settings
const formatTransferDate = (dateValue) => {
  if (!dateValue) return '-';
  return formatDate(dateValue);
};

// Format time for display using regional settings
const formatTransferTime = (dateValue) => {
  if (!dateValue) return '-';
  return formatTime(dateValue);
};

const StockTransferReport = () => {
  const user = getCurrentUser();
  const companyId = user?.companyId || 1;

  // State
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState({});
  
  // Filters
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // Fetch report data
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        startDate: startDate,
        endDate: endDate
      };

      const response = await axiosInstance.get('/reports/stock-transfer', { params });
      
      setTransfers(response.data.transfers || []);
      setSummary(response.data.summary || {});
      toast.success('Report loaded successfully');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
      setTransfers([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Initial load
  useEffect(() => {
    fetchReport();
  }, []);

  // Handlers
  const handleGenerateReport = () => {
    setPage(0);
    fetchReport();
  };

  const handleRefresh = () => {
    fetchReport();
  };

  const handleExportToExcel = () => {
    try {
      const exportData = transfers.map(transfer => ({
        'Date': formatTransferDate(transfer.transactionDate),
        'Time': formatTransferTime(transfer.transactionDate),
        'Reference': transfer.reference || '-',
        'Product': transfer.productName || '-',
        'Product Code': transfer.productCode || '-',
        'Quantity': transfer.quantity || 0,
        'From Location': transfer.fromLocationName || 'Warehouse',
        'To Location': transfer.toLocationName || 'Unknown',
        'Created By': transfer.createdBy || 'System',
        'Notes': transfer.notes || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Stock Transfers');
      XLSX.writeFile(wb, `stock_transfer_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export report');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Paginated transfers
  const paginatedTransfers = transfers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Stock Transfer Report
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportToExcel}
                disabled={transfers.length === 0}
              >
                Export to Excel
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGenerateReport}
                  disabled={loading}
                  sx={{ height: '40px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Summary Cards */}
          {!loading && Object.keys(summary).length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TransferIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        Total Transfers
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.totalTransfers || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        Total Quantity
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.totalQuantityTransferred?.toFixed(2) || '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ProductIcon sx={{ color: 'info.main', mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        Unique Products
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {summary.uniqueProducts || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationIcon sx={{ color: 'warning.main', mr: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        Locations Involved
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {(summary.uniqueFromLocations || 0) + (summary.uniqueToLocations || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Report Table */}
          <Paper elevation={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>From Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>To Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedTransfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No transfers found for the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTransfers.map((transfer, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTransferDate(transfer.transactionDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTransferTime(transfer.transactionDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transfer.reference || '-'} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {transfer.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transfer.productCode}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {transfer.quantity?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transfer.fromLocationName} 
                            size="small" 
                            color="error" 
                            variant="outlined"
                            icon={<LocationIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transfer.toLocationName} 
                            size="small" 
                            color="success" 
                            variant="outlined"
                            icon={<LocationIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transfer.createdBy || 'System'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={transfers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </Paper>
        </Container>
      </Layout>
  );
};

export default StockTransferReport;

